import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { softwareList } from '@/mocks/software';
import { categories } from '@/mocks/categories';
import { filterSoftware, type SortValue } from '@/utils/catalog';
import type { Platform } from '@/types/software';
import SoftwareCard from '@/components/feature/SoftwareCard';
import FilterBar from '@/components/feature/FilterBar';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/feature/Breadcrumb';

const PAGE_STEP = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';

  const [input, setInput] = useState(keyword);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [type, setType] = useState('');
  const [sort, setSort] = useState<SortValue>('downloads');
  const [visible, setVisible] = useState(PAGE_STEP);

  usePageMeta(
    keyword ? `搜索「${keyword}」 - 看片软件` : '软件搜索 - 看片软件',
    keyword
      ? `在「看片软件」搜索「${keyword}」相关的影视播放软件与看片工具下载结果。`
      : '输入关键词，在「看片软件」快速搜索影视播放软件与看片工具。',
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [keyword, platforms, type, sort]);

  const results = useMemo(() => {
    return filterSoftware(softwareList, { keyword, platforms, type, sort });
  }, [keyword, platforms, type, sort]);

  const visibleItems = results.slice(0, visible);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  const togglePlatform = (p: Platform) => {
    setPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const resetFilters = () => {
    setPlatforms([]);
    setType('');
    setSort('downloads');
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ label: '软件搜索' }]} />
      </div>

      <header className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="mb-4 text-xl font-bold text-foreground-950 md:text-2xl">
          {keyword ? `搜索「${keyword}」` : '软件搜索'}
        </h1>
        <form onSubmit={submit}>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-400"></i>
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入软件名称、关键词，如：播放器、解码、安卓…"
                className="w-full bg-transparent px-2 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line"></i>搜索
            </button>
          </div>
        </form>

        {keyword && (
          <p className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            为你找到
            <span className="font-semibold text-accent-600">{results.length}</span>
            款与「{keyword}」相关的软件
          </p>
        )}
      </header>

      <FilterBar
        platforms={platforms}
        onTogglePlatform={togglePlatform}
        type={type}
        onTypeChange={setType}
        updatedWithin=""
        onUpdatedWithinChange={() => {}}
        sort={sort}
        onSortChange={setSort}
        onReset={resetFilters}
        showUpdatedWithin={false}
        resultCount={results.length}
      />

      <div className="mt-6">
        {visibleItems.length === 0 ? (
          <div className="flex flex-col gap-6">
            <EmptyState
              icon="ri-search-eye-line"
              title="没有找到匹配的软件"
              description="试试更换关键词，或调整筛选条件；也可以直接浏览下方分类导航。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-apps-2-line text-primary-600"></i>按分类浏览
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {visibleItems.map((item) => (
                <SoftwareCard key={item.id} item={item} />
              ))}
            </div>

            {visible < results.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_STEP)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {results.length - visible} 款）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}